/*
 * SSE 流式对话 Hook
 *
 * 解析 assistant-ui UIMessageStream 格式（XHeyClaw-Web 后端实际返回）：
 *
 *   data: {"type":"start","messageId":"..."}
 *   data: {"type":"text-start","id":"text-1"}
 *   data: {"type":"text-delta","id":"text-1","delta":"你好"}
 *   data: {"type":"text-end","id":"text-1"}
 *   data: {"type":"reasoning-start","id":"r-1"}
 *   data: {"type":"reasoning-delta","id":"r-1","delta":"..."}
 *   data: {"type":"reasoning-end","id":"r-1"}
 *   data: {"type":"tool-input-start","toolCallId":"...","toolName":"bash"}
 *   data: {"type":"tool-input-delta","toolCallId":"...","inputTextDelta":"..."}
 *   data: {"type":"tool-input-available","toolCallId":"...","input":{...}}
 *   data: {"type":"tool-output-available","toolCallId":"...","output":...}
 *   data: {"type":"tool-output-error","toolCallId":"...","errorText":"..."}
 *   data: {"type":"finish","finishReason":"stop","messageMetadata":{"durationMs":...,"tokenCount":...}}
 *   data: [DONE]
 *
 * 所有 part 落到 ChatMessage.parts[]，由 ChatPanel MessageBubble 分派渲染。
 */

import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import { xheyAi } from '../api'
import type { ChatMessage, MessagePart } from '../types'
import { getTextContent } from '../types'
import { sanitizeAssistantText } from '../utils/sanitizeAssistantText'
// user_ask ActionSheet 派发依赖 —— 老栈用动态 require 规避循环依赖，Vite(ESM)下
// require 不存在会抛，须改静态 import（store.ts/slice 均不 import 本文件，无循环风险）。
import { pickUserAskDisplayAs } from '../utils/userAskDisplayAs'
import { readSessionId } from '../utils/track'
import { store } from '../../../app/store'
import { openActionSheet } from '../../../slices/aiClerkUserAskSlice'

/**
 * 把 SSE / 网络 / LLM 上游的 raw 错误文本翻译成**用户可读**的通用提示.
 *
 * 设计原则: UI 给用户看的文案**绝不能暴露**模型名 / provider / API 路径 /
 * billing / quota / 内部 errorCode 等技术细节; 原文统一走 console.warn 给开发排查.
 *
 * 分类靠 keyword 匹配 raw 文本(不解析 JSON, 因为后端 / 各 provider 错误结构不一致),
 * 命中就映射到对应中性文案; 全 miss → 通用兜底.
 */
function classifyErrorMessage(raw: string): string {
  const s = (raw || '').toLowerCase()
  // 配额 / 计费 / 余额相关 — 阿里云百炼 insufficient_quota / OpenAI exceeded quota / ...
  if (
    /quota|insufficient|exceed|billing|balance|payment|余额|配额|欠费/.test(s)
  ) {
    return 'AI 服务暂时不可用, 请稍后再试'
  }
  // 限流 — 429 / rate limit / too many requests
  if (/rate.?limit|429|too many request|限流|频繁/.test(s)) {
    return '当前访问较多, 请稍候片刻再试'
  }
  // 超时 — timeout / ETIMEDOUT / deadline exceeded
  if (/timeout|timed.?out|deadline|超时/.test(s)) {
    return '响应超时, 请稍后重试'
  }
  // 网络 / 连接 — econnreset / fetch failed / network error
  if (
    /econnreset|econnrefused|enotfound|network|fetch.?fail|connection|网络/.test(
      s
    )
  ) {
    return '网络连接不稳, 请检查后重试'
  }
  // 上下文超长 — context length / token limit
  if (/context.?length|token.?limit|max.?tokens|context window/.test(s)) {
    return '对话内容过长, 请精简后重试'
  }
  // 模型不可用 — model not found / unavailable / 503
  if (/model.?not.?found|model.?unavailable|503|service unavailable/.test(s)) {
    return 'AI 服务繁忙, 请稍后再试'
  }
  // 工具调用本身的 error (e.g. "tool call interrupted") — 保留原文, 一般是工具自己的业务错误
  if (/tool call interrupted/.test(s)) {
    return raw
  }
  // 兜底: 不暴露任何细节
  return 'AI 暂时无响应, 请稍后重试'
}

interface UseSSEChatOptions {
  onChunk?: (chunk: string) => void
  onDone?: (info: { toolNames: string[]; assistantMsgId: string }) => void
  onError?: (err: Error) => void
}

/** 已上传完成的附件（图片 / 文档），会落到用户消息的 file part 上 */
export interface SendAttachment {
  url: string
  name: string
  mediaType: string
}

interface SendParams {
  message: string
  /** composer 输入框上传的结构化附件，对齐 XHeyClaw-Web file part */
  attachments?: SendAttachment[]
  /** 主文档在后端容器内的本地路径，仅首条消息注入 <current-doc> */
  currentDocPath?: string
}

interface SSEChatResult {
  messages: ChatMessage[]
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>
  sending: boolean
  send: (params: SendParams) => Promise<void>
  abort: () => void
  clear: () => void
}

export function useSSEChat(options: UseSSEChatOptions = {}): SSEChatResult {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [sending, setSending] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  // Server 端流首事件 `event: runId\ndata: {"runId": ...}` 抽出, abort 时回传
  // 给 /api/chat/abort. Server 拿 runId 调 cancel_event.set() + openclaw RPC chat.abort.
  // 没 runId 后端没法精确中止 (会跑到 LLM 自然完成, 烧 token).
  const currentRunIdRef = useRef<string | null>(null)

  const send = useCallback(
    async (params: SendParams) => {
      if (sending) return
      setSending(true)

      const abortCtrl = new AbortController()
      abortRef.current = abortCtrl
      const encounteredToolNames = new Set<string>()

      // 看门狗计时器：streaming 改造后 streaming:false 只由"reader 读完"翻，
      // 万一后端发完 finish/error 却不 close 连接，reader.read() 会永久阻塞、
      // UI 永久挂在 streaming 态。计时器声明在 try 外，finally 才能清理它。
      let watchdogTimer: ReturnType<typeof setTimeout> | null = null
      const clearWatchdog = () => {
        if (watchdogTimer) {
          clearTimeout(watchdogTimer)
          watchdogTimer = null
        }
      }

      const userMsgId = `u-${Date.now()}`
      const assistantMsgId = `a-${Date.now()}`
      const thinkingStart = Date.now()

      // 流式文本攒批 (rAF 每帧 flush): text-delta 累积到一帧再一次 setMessages —— 把"每 token
      // 一次重渲 + 每 token 对累积全文重跑 sanitizeAssistantText (O(text²))"降到每帧一次.
      // ⚠️ 任何非 text-delta 事件 (text-start 占位 / tool / text-end 等结构事件) 必须先 flush,
      // 否则缓冲文本会被 appendToLast 落到错误的 part 上 (见 handleStreamEvent text-start 注释).
      // 声明在 try 外, 让 finally 也能兜底 flush + 取消残留 rAF.
      let pendingText = ''
      let pendingTextRaf: number | null = null
      const flushPendingText = () => {
        if (pendingTextRaf != null) {
          cancelAnimationFrame(pendingTextRaf)
          pendingTextRaf = null
        }
        if (!pendingText) return
        const delta = pendingText
        pendingText = ''
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsgId
              ? { ...m, parts: appendToLast(m.parts, 'text', delta) }
              : m
          )
        )
      }

      // ① 推入用户消息 + 占位 AI 消息；把历史转成 parts[] 发给后端
      let historyForBackend: Array<{
        role: 'user' | 'assistant'
        parts: Array<{ type: string; [k: string]: unknown }>
      }> = []
      // 用户消息的 parts：text + 每个附件一个 file part（对齐 XHeyClaw-Web）
      const userParts: any[] = [{ type: 'text', text: params.message }]
      if (params.attachments && params.attachments.length > 0) {
        for (const a of params.attachments) {
          userParts.push({
            type: 'file',
            url: a.url,
            mediaType: a.mediaType,
            name: a.name
          })
        }
      }

      setMessages((prev) => {
        historyForBackend = prev.map((m) => {
          // 历史用户消息：把 file parts 也回放给后端，保证多轮对话里附件还在上下文
          if (m.role === 'user') {
            const hParts: Array<{ type: string; [k: string]: unknown }> = []
            const text = getTextContent(m)
            if (text) hParts.push({ type: 'text', text })
            for (const p of m.parts) {
              if (p.type === 'file') {
                hParts.push({
                  type: 'file',
                  url: p.url,
                  mediaType: p.mediaType,
                  name: p.name
                })
              }
            }
            return {
              role: m.role,
              parts: hParts.length > 0 ? hParts : [{ type: 'text', text: '' }]
            }
          }
          // assistant：只回放 text 部分（reasoning / tool-call 是流式产物）
          return {
            role: m.role,
            parts: [{ type: 'text', text: getTextContent(m) }]
          }
        })
        return [
          ...prev,
          {
            id: userMsgId,
            role: 'user',
            parts: userParts,
            createdAt: Date.now()
          },
          {
            id: assistantMsgId,
            role: 'assistant',
            parts: [],
            streaming: true,
            thinkingStartedAt: thinkingStart,
            createdAt: Date.now()
          }
        ]
      })

      try {
        const res = await xheyAi.chatStream({
          history: historyForBackend,
          userText: params.message,
          attachments: params.attachments,
          currentDocPath: params.currentDocPath
        })

        const reader = res.body?.getReader()
        if (!reader) throw new Error('No response body')
        const decoder = new TextDecoder()
        let buffer = ''

        // staging / prod 没 BFF（setupProxy.js 只跑在 webpack-dev-server），
        // 后端 /api/chat 直接吐 OpenAI delta 格式。这里检测一下：如果 chunk
        // 长得像 OpenAI（有 .choices、没 .type），就走前端 transformer 把它
        // 翻译成 UIMessageStream 事件再喂给 handleStreamEvent。
        // 本地 BFF 已转换好的事件（evt.type 字符串）走透传分支，行为不变。
        let openaiTx: OpenAITransformer | null = null

        // 看门狗：收到 finish / error（"逻辑结束"信号）后武装；之后每个事件
        // 都续期。若 WATCHDOG_MS 内 reader 仍没自然 done，强制 cancel，让
        // read() 返回 done 走正常收尾，避免 UI 永久卡在 streaming 态。
        // 多步 agent 期间事件不断 → 看门狗一直被推后，永远不会误触发。
        const WATCHDOG_MS = 30_000
        let watchdogArmed = false
        const bumpWatchdog = () => {
          if (!watchdogArmed) return
          clearWatchdog()
          watchdogTimer = setTimeout(() => {
            // eslint-disable-next-line no-console
            console.warn(
              '[useSSEChat] 收到 finish/error 后连接迟迟未关闭，强制收尾'
            )
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            reader.cancel().catch(() => {})
          }, WATCHDOG_MS)
        }

        const emit = (evt: any) => {
          // 流式文本攒批: text-delta 累积到一帧 (rAF) 再提交; onChunk 仍逐 token 回调.
          if (evt?.type === 'text-delta') {
            const delta: string = evt.delta || evt.textDelta || ''
            if (!delta) return
            if (options.onChunk) options.onChunk(delta)
            pendingText += delta
            if (pendingTextRaf == null) {
              pendingTextRaf = requestAnimationFrame(flushPendingText)
            }
            return
          }
          // 非 text-delta: 先把缓冲文本提交到当前 part, 保证后续结构事件 (tool / text-start /
          // text-end / finish 等) 落在正确位置.
          flushPendingText()
          // finish / error 都是"逻辑结束"信号 → 武装看门狗
          if (evt && (evt.type === 'finish' || evt.type === 'error')) {
            watchdogArmed = true
          }
          bumpWatchdog()
          handleStreamEvent(
            evt,
            assistantMsgId,
            setMessages,
            options,
            encounteredToolNames
          )
        }

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          if (abortCtrl.signal.aborted) {
            await reader.cancel()
            break
          }

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''

          for (const line of lines) {
            const trimmed = line.trim()
            if (!trimmed) continue
            if (!trimmed.startsWith('data:')) continue
            const dataStr = trimmed.slice(5).trim()
            if (!dataStr) continue
            if (dataStr === '[DONE]') {
              if (openaiTx) openaiTx.end(emit)
              continue
            }

            try {
              const evt = JSON.parse(dataStr)
              // Server SSE 流首事件 `event: runId\ndata: {"runId":"..."}` (service.py:437)
              // — 我们的解析器不读 event: 行, 但 data 里的 JSON 直接拿到 runId. 抽出存
              // ref 供 abort 用. 后续事件不带 runId, 这条只会命中一次.
              if (typeof evt?.runId === 'string') {
                currentRunIdRef.current = evt.runId
              }
              if (isOpenAIChunk(evt)) {
                if (!openaiTx) openaiTx = createOpenAITransformer()
                openaiTx.consume(evt, emit)
              } else {
                emit(evt)
              }
            } catch (e) {
              // eslint-disable-next-line no-console
              console.warn('[useSSEChat] 解析失败:', dataStr, e)
            }
          }
        }
        // 流读完但没碰到 [DONE]：兜底 flush 一次 transformer
        if (openaiTx) openaiTx.end(emit)
        flushPendingText() // 提交最后一帧未及 flush 的缓冲文本 (done / abort 路径都经此)

        // 流真正读完（reader done）才是 streaming:false 的唯一权威信号。
        // 带内 finish / error 事件不再翻 streaming —— agent 多步执行时后端
        // 会在中途发 finish（某个 LLM step 结束）或可恢复的 error，此时 SSE
        // 连接还开着、内容还在流，提前置 false 会让左侧 docActionState 的
        // editing 态瞬间塌掉（左侧 loading 在 SSE 没完成时就消失）。
        // durationMs 兜底：finish 没来时用 thinkingStartedAt 估算，不丢耗时。
        //
        // ⚠️ 空消息兜底: 后端 LLM 上游 error (e.g. quota / network) 时, 链路实现
        // 可能不发 SSE error event 就直接关流, 此时 assistant message 是空气消息
        // (parts.length === 0 或全是不可见 reasoning), 用户看着像"AI 卡了没动".
        // 这里检测兜底, 追加一条通用错误提示, 跟 case 'error' 走同一 classifier.
        setMessages((prev) =>
          prev.map((m) => {
            if (m.id !== assistantMsgId) return m
            // 判定"完全没有可见输出": 没文字 / 没工具调用 / 没 user-ask
            // / 没已存在的 system-error
            // (reasoning-only 不算可见, LLM 思考了但没说话也属于异常)
            const hasVisibleOutput = m.parts.some(
              (p) =>
                (p.type === 'text' && p.text.trim().length > 0) ||
                p.type === 'tool-call' ||
                p.type === 'user-ask' ||
                p.type === 'system-error'
            )
            const nextParts = hasVisibleOutput
              ? m.parts
              : [
                  ...m.parts,
                  {
                    type: 'system-error' as const,
                    message: classifyErrorMessage('')
                  }
                ]
            return {
              ...m,
              parts: nextParts,
              streaming: false,
              durationMs:
                m.durationMs ??
                (m.thinkingStartedAt
                  ? Date.now() - m.thinkingStartedAt
                  : undefined)
            }
          })
        )
        if (options.onDone) {
          options.onDone({
            toolNames: Array.from(encounteredToolNames),
            assistantMsgId
          })
        }
      } catch (err) {
        const errObj = err instanceof Error ? err : new Error(String(err))
        // 用户主动 abort (点停止) → reader 抛 AbortError, 不是错误: 不弹错误卡.
        // 消息 + 在跑的 tool-call 已由 abort() 同步收尾, 这里直接 swallow.
        if (errObj.name !== 'AbortError') {
          // 内部诊断写 console; 用户可见走 classifier
          // eslint-disable-next-line no-console
          console.warn(
            '[useSSEChat] stream caught error (raw):',
            errObj.message
          )
          const friendlyMsg = classifyErrorMessage(errObj.message)
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== assistantMsgId) return m
              // 已经有 system-error 卡了不重复加 (case 'error' 已添加 + reader 兜底
              // 也会跑, 避免叠多张卡)
              const hasErrorCard = m.parts.some(
                (p) => p.type === 'system-error'
              )
              const nextParts: MessagePart[] = hasErrorCard
                ? m.parts
                : [...m.parts, { type: 'system-error', message: friendlyMsg }]
              return { ...m, parts: nextParts, streaming: false }
            })
          )
          if (options.onError) options.onError(errObj)
        }
      } finally {
        flushPendingText() // error 路径兜底 + 取消残留 rAF (done/abort 已 flush 过, 幂等)
        clearWatchdog()
        setSending(false)
        abortRef.current = null
        currentRunIdRef.current = null
      }
    },
    [sending, options]
  )

  const abort = useCallback(() => {
    // 拿 runId 必须在清 ref 之前
    const runId = currentRunIdRef.current
    // 1. 关本地 SSE reader (浏览器侧停接收, 输入框立刻解锁)
    if (abortRef.current) abortRef.current.abort()
    abortRef.current = null
    setSending(false)
    currentRunIdRef.current = null
    // 1.5 立刻收尾 UI: reader 被 abort 后不再发 tool-output 事件, 仍卡在
    //     input-available 的 tool-call 会让 spinner 永转 (ToolCallGroup.isRunning
    //     只看 part.state, 不看 message.streaming). 把仍 streaming 的那条消息 +
    //     其中在跑的 tool-call 当场翻成终态, spinner 立即停 (= 真"立马停").
    setMessages((prev) =>
      prev.map((m) =>
        m.streaming
          ? {
              ...m,
              streaming: false,
              parts: m.parts.map((p) =>
                p.type === 'tool-call' &&
                (p.state === 'input-streaming' || p.state === 'input-available')
                  ? { ...p, state: 'output-available' as const }
                  : p
              )
            }
          : m
      )
    )
    // 2. 通知 Server 真停 agent loop (不 await, 网络挂了 chatAbort 自己 console.warn)
    // —— 不发这个的话 Server 端 agent loop 继续烧 token 跑到 LLM 自然完成,
    //    会话列表的"思考中"状态不会立刻消失 (session metadata 还 isRunning).
    if (runId) {
      xheyAi.chatAbort(runId).catch(() => {
        /* swallow — chatAbort 已自己 warn */
      })
    }
  }, [])

  const clear = useCallback(() => {
    abort()
    setMessages([])
  }, [abort])

  // ─── 退出页面 → 自动 abort 旧 run (避免 zombie 在后端继续烧 token) ───
  //
  // 三个退出场景:
  //   1. 关闭 tab / 浏览器                 → pagehide 触发
  //   2. F5 刷新 / 输入新 URL 跳出 SPA    → pagehide 触发
  //   3. 组件 unmount (React Router 切走) → effect cleanup 触发
  //
  // 区别: pagehide 时 page 整体走 unload, normal fetch 会被浏览器 cancel ——
  // 必须用 chatAbortBeacon (fetch with `keepalive: true`, sync 凑 header).
  // 组件 unmount 时 page 还活着, 走正常 async xheyAi.chatAbort.
  useEffect(() => {
    const handlePageHide = () => {
      if (currentRunIdRef.current) {
        xheyAi.chatAbortBeacon(currentRunIdRef.current)
      }
    }
    window.addEventListener('pagehide', handlePageHide)
    return () => {
      window.removeEventListener('pagehide', handlePageHide)
      // 组件 unmount: page 还活着, 走正常 abort 路径 (含完整 header / token 续签)
      if (currentRunIdRef.current) {
        xheyAi.chatAbort(currentRunIdRef.current).catch(() => {
          /* swallow */
        })
      }
    }
  }, [])

  return { messages, setMessages, sending, send, abort, clear }
}

// ─── OpenAI delta → UIMessageStream 翻译（前端兜底） ────────
//
// 背景：本地 webpack-dev-server 的 setupProxy.js 有个 forwardChatWithTransform
// 把后端裸 OpenAI SSE 转成 assistant-ui UIMessageStream（text-* / reasoning-*
// / tool-input-* / tool-output-* / finish）。staging / prod 上是静态 SPA，
// 没 BFF，nginx 直连后端，前端拿到的是裸 OpenAI delta，handleStreamEvent
// 的 switch 全 miss → 页面不渲染。
//
// 这里把 XHeyClaw-Web/app/api/chat/route.ts 的转换逻辑 1:1 复刻到前端，
// 检测到裸 OpenAI chunk 就自己翻译成 UIMessageStream 事件再喂回 switch。
// 本地 BFF 路径走 evt.type 分支，不触发本逻辑。

/** 裸 OpenAI delta chunk 的形状判别：有 choices 数组、且没有 UIMessageStream 的 type 字段 */
function isOpenAIChunk(evt: any): boolean {
  return (
    evt &&
    typeof evt === 'object' &&
    typeof evt.type !== 'string' &&
    Array.isArray(evt.choices)
  )
}

interface OpenAITransformer {
  consume(chunk: any, emit: (evt: any) => void): void
  /** [DONE] 或流关闭时调用：flush pending tools + emit finish */
  end(emit: (evt: any) => void): void
}

function createOpenAITransformer(): OpenAITransformer {
  const textId = `text-${Date.now()}`
  const reasoningId = `reasoning-${Date.now()}`
  let textStarted = false
  let reasoningStarted = false
  let toolIdx = 0
  let outputChars = 0
  let finished = false
  const startTime = Date.now()
  const pendingTools = new Map<
    string,
    { name: string; args: string; inputSent: boolean }
  >()

  const emitToolInputAvailable = (callId: string, emit: (evt: any) => void) => {
    let tool = pendingTools.get(callId)
    if (!tool) {
      // 上游先发了 xhey_tool_call_status 而没 tool_calls chunk → 补一个最小 start
      emit({ type: 'tool-input-start', toolCallId: callId, toolName: 'tool' })
      tool = { name: 'tool', args: '', inputSent: false }
      pendingTools.set(callId, tool)
    }
    if (tool.inputSent) return
    let parsedArgs: unknown = {}
    try {
      parsedArgs = tool.args ? JSON.parse(tool.args) : {}
    } catch {
      parsedArgs = tool.args
    }
    emit({
      type: 'tool-input-available',
      toolCallId: callId,
      toolName: tool.name,
      input: parsedArgs
    })
    tool.inputSent = true
  }

  /** 残留工具：流关时强制收尾，标记成 error 而不是 completed，避免 UI 假装成功 */
  const flushPendingTools = (emit: (evt: any) => void) => {
    const ids = Array.from(pendingTools.keys())
    for (const callId of ids) {
      emitToolInputAvailable(callId, emit)
      emit({
        type: 'tool-output-error',
        toolCallId: callId,
        errorText: 'tool call interrupted'
      })
    }
    pendingTools.clear()
  }

  return {
    consume(chunk, emit) {
      if (chunk?.error) {
        emit({
          type: 'error',
          errorText:
            typeof chunk.error === 'string'
              ? chunk.error
              : JSON.stringify(chunk.error)
        })
        return
      }
      const choice = chunk?.choices?.[0]
      if (!choice) return
      const delta = choice.delta ?? {}
      const finishReason = choice.finish_reason

      // 1) reasoning_content（通义千问 / Dashscope 扩展字段）
      const reasoning = delta.reasoning_content || delta.reasoning || ''
      if (reasoning) {
        if (!reasoningStarted) {
          emit({ type: 'reasoning-start', id: reasoningId })
          reasoningStarted = true
        }
        emit({ type: 'reasoning-delta', id: reasoningId, delta: reasoning })
      }

      // 2) tool_calls 增量累积
      if (delta.tool_calls) {
        if (reasoningStarted) {
          emit({ type: 'reasoning-end', id: reasoningId })
          reasoningStarted = false
        }
        if (textStarted) {
          emit({ type: 'text-end', id: textId })
          textStarted = false
        }
        for (const tc of delta.tool_calls) {
          const func = tc.function ?? {}
          const callId = tc.id || `tool-${toolIdx}`
          if (func.name) {
            const newCallId = tc.id || `tool-${toolIdx++}`
            const existing = pendingTools.get(newCallId)
            if (existing) {
              // 之前 xhey_tool_call_status 已经预创建过 stub（toolName='tool'），
              // 这里只升级 name，**不再 emit 第二个 tool-input-start**，
              // 否则 ToolCallGroup 里会出现两个相同 toolCallId 的 part
              // 撞 React key
              if (existing.name === 'tool') existing.name = func.name
            } else {
              emit({
                type: 'tool-input-start',
                toolCallId: newCallId,
                toolName: func.name
              })
              pendingTools.set(newCallId, {
                name: func.name,
                args: '',
                inputSent: false
              })
            }
          }
          if (func.arguments) {
            const pending = pendingTools.get(callId)
            if (pending) {
              pending.args += func.arguments
            } else {
              pendingTools.set(callId, {
                name: func.name ?? 'tool',
                args: func.arguments,
                inputSent: false
              })
            }
            // 累加到合法 JSON 立刻 emit tool-input-available（让 running 状态
            // 也能展示完整参数，不用等 [DONE]）
            const cur = pendingTools.get(callId)
            if (cur && !cur.inputSent && cur.args) {
              try {
                JSON.parse(cur.args)
                emitToolInputAvailable(callId, emit)
              } catch {
                /* 还在累加 */
              }
            }
          }
        }
      }

      // 3) OpenClaw 扩展：xhey_tool_call_status
      //    status=running  → 只带 UI 元信息（activityDescription / toolUseSummary），
      //      工具还在跑 —— 发 tool-meta 事件，**不**结束工具、**不** delete。
      //      (隐患修复：以前 running 落进 else 分支被当成 completed，会把工具提前标记成 done)
      //    status=completed/failed → 工具真终态 —— 照旧 emit output，并带上
      //      后端的 toolUseSummary / durationMs。
      const ts = delta.xhey_tool_call_status
      if (ts && typeof ts === 'object') {
        const id =
          (typeof ts.id === 'string' && ts.id) ||
          (typeof ts.toolCallId === 'string' && ts.toolCallId) ||
          null
        if (id) {
          if (ts.status === 'running') {
            emit({
              type: 'tool-meta',
              toolCallId: id,
              ...(typeof ts.activityDescription === 'string'
                ? { activityDescription: ts.activityDescription }
                : {}),
              ...(typeof ts.toolUseSummary === 'string'
                ? { toolUseSummary: ts.toolUseSummary }
                : {}),
              ...(ts.progress &&
              typeof ts.progress === 'object' &&
              typeof ts.progress.done === 'number' &&
              typeof ts.progress.total === 'number'
                ? { progress: ts.progress }
                : {})
            })
          } else {
            emitToolInputAvailable(id, emit)
            const meta = {
              ...(typeof ts.toolUseSummary === 'string'
                ? { toolUseSummary: ts.toolUseSummary }
                : {}),
              ...(typeof ts.durationMs === 'number'
                ? { durationMs: ts.durationMs }
                : {})
            }
            if (ts.status === 'failed') {
              emit({
                type: 'tool-output-error',
                toolCallId: id,
                errorText:
                  typeof ts.error === 'string' && ts.error
                    ? ts.error
                    : 'tool call failed',
                ...meta
              })
            } else {
              emit({
                type: 'tool-output-available',
                toolCallId: id,
                output: ts.output !== undefined ? ts.output : 'completed',
                ...meta
              })
            }
            pendingTools.delete(id)
          }
        }
      }

      // 3.5) xhey_user_ask: Phase 2 structured interaction
      const userAsk = delta.xhey_user_ask
      if (userAsk && typeof userAsk === 'object') {
        emit({ type: 'user-ask', data: userAsk })
      }

      // 4) 文本
      if (delta.content) {
        if (reasoningStarted) {
          emit({ type: 'reasoning-end', id: reasoningId })
          reasoningStarted = false
        }
        if (!textStarted) {
          emit({ type: 'text-start', id: textId })
          textStarted = true
        }
        outputChars += delta.content.length
        emit({ type: 'text-delta', id: textId, delta: delta.content })
      }

      // 5) finish_reason：tool_calls 不是真结束（后面还有第二轮 text）；
      //    其他 reason → 关闭 reasoning / text，但不 emit finish（在 end() 里发）
      if (finishReason && finishReason !== 'tool_calls') {
        flushPendingTools(emit)
        if (reasoningStarted) {
          emit({ type: 'reasoning-end', id: reasoningId })
          reasoningStarted = false
        }
        if (textStarted) {
          emit({ type: 'text-end', id: textId })
          textStarted = false
        }
      }
    },
    end(emit) {
      if (finished) return
      finished = true
      flushPendingTools(emit)
      if (reasoningStarted) {
        emit({ type: 'reasoning-end', id: reasoningId })
        reasoningStarted = false
      }
      if (textStarted) {
        emit({ type: 'text-end', id: textId })
        textStarted = false
      }
      emit({
        type: 'finish',
        finishReason: 'stop',
        messageMetadata: {
          timing: {
            streamStartTime: startTime,
            totalStreamTime: Date.now() - startTime,
            // chars/2 是 token 粗估，与 XHeyClaw-Web 保持一致
            tokenCount: Math.ceil(outputChars / 2),
            promptTokens: 0,
            totalChunks: 0,
            toolCallCount: toolIdx
          }
        }
      })
    }
  }
}

// ─── 辅助：按 toolCallId 找到 part 并 patch ───
function patchToolCall(
  parts: MessagePart[],
  toolCallId: string,
  patch: Partial<Extract<MessagePart, { type: 'tool-call' }>>
): MessagePart[] {
  return parts.map((p) =>
    p.type === 'tool-call' && p.toolCallId === toolCallId
      ? ({ ...p, ...patch } as MessagePart)
      : p
  )
}

// ─── 辅助：把 delta 追加到最后一个指定 type 的 part（text/reasoning） ───
function appendToLast(
  parts: MessagePart[],
  type: 'text' | 'reasoning',
  delta: string
): MessagePart[] {
  if (!delta) return parts
  // 只看 parts 最后一项：避免后端在同一轮里复用 text/reasoning id（例如
  // text1 → tool → text2 复用同 id）时，delta 跨过中间的 tool-call 反向
  // 回填到 text1 上，把 text2 的内容（表格 / 分析）错误地拼进 text1，
  // 视觉上时序错乱（工具调用被挤到正文之后）。
  const last = parts[parts.length - 1]
  if (last && last.type === type) {
    const next = [...parts]
    const rawText = last.text + delta
    // ⚡ 资料员专业感 sanitize hook (仅 text part, reasoning 不动):
    // 流式累加后跑一遍 sanitize, 把 LLM 偶发说出的弱化词 / 内部术语 / hidden
    // marker 从用户可见文本里剪掉. idempotent — 每 chunk 都重跑同样结果.
    // 详见 utils/sanitizeAssistantText.ts.
    const finalText = type === 'text' ? sanitizeAssistantText(rawText) : rawText
    next[next.length - 1] = { ...last, text: finalText } as MessagePart
    return next
  }
  // 最后一项不是同类型 → push 一个新 part
  const cleanedDelta = type === 'text' ? sanitizeAssistantText(delta) : delta
  return [
    ...parts,
    type === 'text'
      ? { type: 'text', text: cleanedDelta }
      : { type: 'reasoning', text: cleanedDelta, state: 'streaming' }
  ]
}

// ─── assistant-ui UIMessageStream 事件处理 ────────────
function handleStreamEvent(
  evt: any,
  assistantMsgId: string,
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>,
  options: UseSSEChatOptions,
  encounteredToolNames: Set<string>
) {
  switch (evt.type) {
    case 'start':
    case 'start-step':
    case 'finish-step':
      break

    case 'text-start': {
      // 立即 push 一个空 text part 占位。后端可能在同一轮里发出
      // text1 → tool → text2（且 text1/text2 复用同一个 id），如果这里
      // 不占位，text2 的 delta 会被 appendToLast 回填到 text1 上，造成
      // 工具调用顺序错乱。占位 part 在最后渲染时若 text 仍为空可由展示
      // 层忽略。
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsgId
            ? { ...m, parts: [...m.parts, { type: 'text', text: '' }] }
            : m
        )
      )
      break
    }

    case 'text-delta': {
      const delta: string = evt.delta || evt.textDelta || ''
      if (!delta) break
      if (options.onChunk) options.onChunk(delta)
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsgId
            ? { ...m, parts: appendToLast(m.parts, 'text', delta) }
            : m
        )
      )
      break
    }

    case 'text-end':
      break

    case 'reasoning-start': {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsgId
            ? {
                ...m,
                parts: [
                  ...m.parts,
                  { type: 'reasoning', text: '', state: 'streaming' }
                ]
              }
            : m
        )
      )
      break
    }

    case 'reasoning-delta': {
      const delta: string = evt.delta || evt.reasoningDelta || ''
      if (!delta) break
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsgId
            ? { ...m, parts: appendToLast(m.parts, 'reasoning', delta) }
            : m
        )
      )
      break
    }

    case 'reasoning-end': {
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== assistantMsgId) return m
          const nextParts = [...m.parts]
          for (let i = nextParts.length - 1; i >= 0; i--) {
            if (nextParts[i].type === 'reasoning') {
              nextParts[i] = { ...nextParts[i], state: 'done' } as MessagePart
              break
            }
          }
          return { ...m, parts: nextParts }
        })
      )
      break
    }

    case 'tool-input-start': {
      const toolCallId: string = evt.toolCallId
      const toolName: string = evt.toolName || ''
      if (toolName) encounteredToolNames.add(toolName)
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== assistantMsgId) return m
          // 幂等：同 toolCallId 已经存在就只 patch toolName（首条 stub 名 'tool'
          // 升级成真实工具名），不再 push 第二个，避免 ToolCallGroup 撞 React key
          const existingIdx = m.parts.findIndex(
            (p) => p.type === 'tool-call' && p.toolCallId === toolCallId
          )
          if (existingIdx >= 0) {
            const next = [...m.parts]
            const cur = next[existingIdx] as Extract<
              MessagePart,
              { type: 'tool-call' }
            >
            // 只升级占位名（'tool'）；真实名已存在不覆盖
            if (toolName && (!cur.toolName || cur.toolName === 'tool')) {
              next[existingIdx] = { ...cur, toolName }
            }
            return { ...m, parts: next }
          }
          return {
            ...m,
            parts: [
              ...m.parts,
              {
                type: 'tool-call',
                toolCallId,
                toolName,
                state: 'input-streaming',
                startedAt: Date.now()
              }
            ]
          }
        })
      )
      break
    }

    case 'tool-meta': {
      // running 状态的 xhey_tool_call_status 带来的 UI 元信息（动态操作描述 /
      // 紧凑摘要）。running chunk 可能先于 tool-input-start 到（后端约定顺序）
      // → 此时 part 还不存在，建一个占位 stub（toolName='tool'），后续
      // tool-input-start 会把名字升级、并保留这里 patch 上的字段。
      const toolCallId: string = evt.toolCallId
      const metaPatch: Partial<Extract<MessagePart, { type: 'tool-call' }>> = {}
      if (
        typeof evt.activityDescription === 'string' &&
        evt.activityDescription
      ) {
        metaPatch.activityDescription = evt.activityDescription
      }
      if (typeof evt.toolUseSummary === 'string' && evt.toolUseSummary) {
        metaPatch.toolUseSummary = evt.toolUseSummary
      }
      if (
        evt.progress &&
        typeof evt.progress === 'object' &&
        typeof evt.progress.done === 'number' &&
        typeof evt.progress.total === 'number'
      ) {
        metaPatch.progress = {
          done: evt.progress.done,
          total: evt.progress.total,
          ...(typeof evt.progress.label === 'string' && evt.progress.label
            ? { label: evt.progress.label }
            : {})
        }
      }
      if (Object.keys(metaPatch).length === 0) break
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== assistantMsgId) return m
          const idx = m.parts.findIndex(
            (p) => p.type === 'tool-call' && p.toolCallId === toolCallId
          )
          if (idx >= 0) {
            const next = [...m.parts]
            next[idx] = { ...next[idx], ...metaPatch } as MessagePart
            return { ...m, parts: next }
          }
          return {
            ...m,
            parts: [
              ...m.parts,
              {
                type: 'tool-call',
                toolCallId,
                toolName: 'tool',
                state: 'input-streaming',
                startedAt: Date.now(),
                ...metaPatch
              }
            ]
          }
        })
      )
      break
    }

    case 'tool-input-delta': {
      const toolCallId: string = evt.toolCallId
      const delta: string = evt.inputTextDelta || evt.delta || ''
      if (!delta) break
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== assistantMsgId) return m
          const nextParts = m.parts.map((p) => {
            if (p.type !== 'tool-call' || p.toolCallId !== toolCallId) return p
            return { ...p, argsText: (p.argsText || '') + delta } as MessagePart
          })
          return { ...m, parts: nextParts }
        })
      )
      break
    }

    case 'tool-input-available': {
      const toolCallId: string = evt.toolCallId
      const toolName: string = evt.toolName || ''
      if (toolName) encounteredToolNames.add(toolName)
      const patch: Partial<Extract<MessagePart, { type: 'tool-call' }>> = {
        args: evt.input || evt.args,
        state: 'input-available'
      }
      if (toolName) patch.toolName = toolName
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsgId
            ? {
                ...m,
                parts: patchToolCall(m.parts, toolCallId, patch)
              }
            : m
        )
      )
      break
    }

    case 'tool-output-available': {
      const toolCallId: string = evt.toolCallId
      const patch: Partial<Extract<MessagePart, { type: 'tool-call' }>> = {
        output: evt.output,
        state: 'output-available',
        endedAt: Date.now()
      }
      // 后端真终态带的紧凑摘要 / 真实耗时（有就用，没有就保持原样）
      if (typeof evt.toolUseSummary === 'string' && evt.toolUseSummary) {
        patch.toolUseSummary = evt.toolUseSummary
      }
      if (typeof evt.durationMs === 'number') {
        patch.durationMs = evt.durationMs
      }
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsgId
            ? { ...m, parts: patchToolCall(m.parts, toolCallId, patch) }
            : m
        )
      )
      break
    }

    case 'tool-output-error':
    case 'tool-error': {
      const toolCallId: string = evt.toolCallId
      const errorText: string =
        (typeof evt.errorText === 'string' && evt.errorText) ||
        (typeof evt.error === 'string' && evt.error) ||
        (evt.error && JSON.stringify(evt.error)) ||
        '出错了'
      const patch: Partial<Extract<MessagePart, { type: 'tool-call' }>> = {
        state: 'output-error',
        errorText,
        endedAt: Date.now()
      }
      if (typeof evt.toolUseSummary === 'string' && evt.toolUseSummary) {
        patch.toolUseSummary = evt.toolUseSummary
      }
      if (typeof evt.durationMs === 'number') {
        patch.durationMs = evt.durationMs
      }
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsgId
            ? { ...m, parts: patchToolCall(m.parts, toolCallId, patch) }
            : m
        )
      )
      break
    }

    case 'finish': {
      // 注意：finish 是「带内」事件，agent 多步执行时后端可能在中途发出
      // （某个 LLM step 结束 ≠ 整条 SSE 结束）。这里只更新元数据，
      // **不碰 streaming** —— streaming 只由 send() 里 reader 读完那段兜底翻
      // false，否则左侧 editing 态会在流还没完时提前塌掉。
      const meta = evt.messageMetadata || {}
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsgId
            ? {
                ...m,
                durationMs:
                  typeof meta.totalStreamTime === 'number'
                    ? meta.totalStreamTime
                    : typeof meta.durationMs === 'number'
                      ? meta.durationMs
                      : m.thinkingStartedAt
                        ? Date.now() - m.thinkingStartedAt
                        : undefined,
                tokenCount:
                  typeof meta.tokenCount === 'number'
                    ? meta.tokenCount
                    : undefined,
                promptTokens:
                  typeof meta.promptTokens === 'number'
                    ? meta.promptTokens
                    : undefined
              }
            : m
        )
      )
      break
    }

    case 'error': {
      // 同 finish：error 也是带内事件，可能是「可恢复」错误（某个工具失败，
      // agent 会继续重试）。只追加错误文本，**不碰 streaming** —— 真正致命的
      // 错误会让 SSE 连接关闭，reader 读完后兜底逻辑自然会把 streaming 翻 false。
      const rawErrMsg = evt.errorText || evt.message || ''
      // 内部诊断: 完整原文写 console 供开发者排查 (含 LLM provider / model / quota
      // / billing 等细节); 用户可见文案走 classifier 中性化
      if (rawErrMsg) {
        // eslint-disable-next-line no-console
        console.warn('[useSSEChat] SSE error event (raw):', rawErrMsg)
      }
      const friendlyMsg = classifyErrorMessage(rawErrMsg)
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsgId
            ? {
                ...m,
                parts: [
                  ...m.parts,
                  { type: 'system-error', message: friendlyMsg }
                ]
              }
            : m
        )
      )
      break
    }

    case 'user-ask': {
      const d = evt.data
      if (!d || typeof d !== 'object') break
      const incomingPid: string = d.pending_id || ''
      const questions = Array.isArray(d.questions) ? d.questions : []
      const autoFillPreview = Array.isArray(d.auto_fill_preview)
        ? d.auto_fill_preview
        : undefined
      // 协议分流(按 title 关键词匹配特例白名单):
      // - 'inline' — title 命中"保存模板"等关键词 → chat 流原位 UserAskBlock
      // - 'chip'   — 默认,所有其他 user_ask → chat 流轻量 chip + 底部 ActionSheet
      const displayAs = pickUserAskDisplayAs(questions, d.title) as
        | 'inline'
        | 'chip'
      if (displayAs === 'chip') {
        // ActionSheet 独立层场景才 dispatch store
        try {
          store.dispatch(
            openActionSheet({
              pendingId: incomingPid,
              title: d.title || '',
              description: d.description,
              questions,
              autoFillPreview,
              sessionId: readSessionId()
            })
          )
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(
            '[useSSEChat] aiClerkUserAsk store dispatch 失败,降级 inline 渲染:',
            e
          )
        }
      }
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== assistantMsgId) return m
          // 后端在同一会话里偶尔会把 xhey_user_ask 跨多个 LLM 步重复 emit
          // (或 SSE chunk 重传),这里按 pending_id 去重,避免连续两张卡片.
          if (
            incomingPid &&
            m.parts.some(
              (p) => p.type === 'user-ask' && p.pendingId === incomingPid
            )
          ) {
            return m
          }
          return {
            ...m,
            parts: [
              ...m.parts,
              {
                type: 'user-ask' as const,
                pendingId: incomingPid,
                title: d.title || '',
                description: d.description,
                questions,
                autoFillPreview,
                displayAs
              }
            ]
          }
        })
      )
      break
    }

    default:
      // 其他类型（如 source-url / file / message-metadata-only）先忽略
      break
  }
}
